import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ChevronLeft, ChevronRight, Upload, Video } from 'lucide-react';

// Mockup files are named "<design>-left.jpg", "<design>-front.jpg", etc. A
// file without a view word is the flat artwork. Views sort into this order,
// and the flat artwork goes last.
const VIEW_ORDER = ['left', 'front', 'back', 'right'];
const MAX_VIDEO_MB = 100;

export function splitViewName(filename) {
  const base = filename.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[-_\s]+/g, ' ').trim();
  const match = base.match(/^(.*) (left|right|front|back)$/);
  return match ? { key: match[1], view: match[2] } : { key: base, view: 'full' };
}

export function viewRank(filename) {
  const { view } = splitViewName(filename);
  const rank = VIEW_ORDER.indexOf(view);
  return rank === -1 ? VIEW_ORDER.length : rank;
}

const thumbButton = {
  background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', borderRadius: '50%',
  width: '22px', height: '22px', cursor: 'pointer', padding: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center'
};

// Admin editor for a mugg's photos (first = main) and optional spin video.
function MuggPhotos({ images, onChange, videoUrl, onVideoChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const addPhotos = async (event) => {
    const files = Array.from(event.target.files || []).filter((f) => f.type.startsWith('image/'));
    event.target.value = '';
    if (!files.length) return;
    files.sort((a, b) => viewRank(a.name) - viewRank(b.name));

    setUploading(true);
    const body = new FormData();
    files.forEach((f) => body.append('files', f));
    try {
      const response = await axios.post('/upload/multiple', body, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange([...images, ...response.data.uploaded.map((u) => u.url)]);
      if (response.data.errors?.length) toast.error(`${response.data.errors.length} photo(s) failed to upload`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= images.length || from === to) return;
    const next = [...images];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const addVideo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error(`Video too large. Maximum size: ${MAX_VIDEO_MB}MB`);
      return;
    }
    setUploadingVideo(true);
    const body = new FormData();
    body.append('file', file);
    try {
      const response = await axios.post('/upload/image', body, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onVideoChange(response.data.url);
      toast.success('Video uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Video upload failed');
    } finally {
      setUploadingVideo(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
        {images.map((url, index) => (
          <div
            key={`${url}-${index}`}
            draggable
            onDragStart={() => setDragIndex(index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (dragIndex !== null) move(dragIndex, index); setDragIndex(null); }}
            onDragEnd={() => setDragIndex(null)}
            style={{
              position: 'relative', width: '110px', height: '110px', borderRadius: '8px', overflow: 'hidden',
              background: '#f3f3f3', cursor: 'grab', opacity: dragIndex === index ? 0.4 : 1,
              border: index === 0 ? '2px solid #1a1a1a' : '1px solid #ddd'
            }}
          >
            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
            {index === 0 && (
              <span style={{
                position: 'absolute', top: '4px', left: '4px', background: '#1a1a1a', color: '#fff',
                fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '4px'
              }}>Main</span>
            )}
            <button type="button" title="Remove" onClick={() => onChange(images.filter((_, i) => i !== index))}
              style={{ ...thumbButton, position: 'absolute', top: '4px', right: '4px' }}>
              <X size={12} />
            </button>
            <div style={{ position: 'absolute', bottom: '4px', left: '4px', right: '4px', display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" title="Move left" onClick={() => move(index, index - 1)}
                style={{ ...thumbButton, visibility: index === 0 ? 'hidden' : 'visible' }}>
                <ChevronLeft size={14} />
              </button>
              <button type="button" title="Move right" onClick={() => move(index, index + 1)}
                style={{ ...thumbButton, visibility: index === images.length - 1 ? 'hidden' : 'visible' }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        ))}

        <label style={{
          width: '110px', height: '110px', borderRadius: '8px', border: '2px dashed #ccc',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer', color: '#888', fontSize: '12px', textAlign: 'center'
        }}>
          <Upload size={20} style={{ marginBottom: '4px' }} />
          {uploading ? 'Uploading…' : 'Add photos'}
          <input type="file" accept="image/*" multiple onChange={addPhotos} disabled={uploading} style={{ display: 'none' }} />
        </label>
      </div>
      <p style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
        Select several at once. Drag (or use the arrows) to reorder — the first photo is the main one in the shop,
        cart and emails. Files named …-left, …-front, …-back, …-right line up in that order, with the flat artwork last.
      </p>

      <div style={{ marginTop: '14px' }}>
        <label style={{ fontWeight: 600 }}>Spin video (optional)</label>
        {videoUrl ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '8px' }}>
            <video src={videoUrl} controls muted playsInline style={{ width: '220px', borderRadius: '8px', background: '#000' }} />
            <button type="button" onClick={() => onVideoChange(null)}
              style={{ background: 'none', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '13px' }}>
              Remove video
            </button>
          </div>
        ) : (
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '8px', padding: '8px 14px',
            border: '1px solid #ccc', borderRadius: '6px', cursor: uploadingVideo ? 'not-allowed' : 'pointer', fontSize: '14px'
          }}>
            <Video size={16} />
            {uploadingVideo ? 'Uploading video…' : 'Upload video'}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={addVideo} disabled={uploadingVideo} style={{ display: 'none' }} />
          </label>
        )}
        <p style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
          A short muted loop (6–10 seconds) works best. MP4 plays in every browser; some iPhone .mov files won't play in Chrome.
          Shoppers see it only when they tap the video thumbnail.
        </p>
      </div>
    </div>
  );
}

export default MuggPhotos;
